import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';

import { Button, Modal } from '@salesforce/design-system-react';

import { AssetPicker } from '../index';
import { FlagContext } from '../../../flags/Flags';
import { withSession } from '../../../i18n/containers/AppInit.js';

import * as Util from '../../../common/utilities';

import './SelectFallbackWizard.scss';

class SelectFallbackWizard extends Component {
  handleFinish(session) {
    return () => {
      this.props.actions.setFallbackAsset(
        this.assetType().type,
        this.getCurrentFallbackAsset(),
        { cToken: session.cToken }
      );
      this.close();
    };
  }

  componentDidUpdate(prevProps) {
    if (Util.propChanged(prevProps, this.props, 'fallback.isClearing', false)) {
      this.props.actions.getAssetTypes();
      this.close();
    }
  }

  handleClearAll(session) {
    return () => {
      this.props.actions.clearFallbackAssets(this.assetType().uuid, {
        cToken: session.cToken
      });
    };
  }

  handleRowSelect(selection) {
    const selectedRow = selection.length ? selection[0] : null;
    this.props.actions.updateModal('selectFallback', { selectedRow });
  }

  close() {
    this.props.actions.closeModal('selectFallback');
  }

  isOpen() {
    return !!this.props.modals.selectFallback?.isOpen;
  }

  assetType() {
    return this.props.modals.selectFallback?.data?.assetType || {};
  }

  getSavedFallbackAsset() {
    return this.props.modals.selectFallback?.data?.assetType?.fallback;
  }

  getCurrentFallbackAsset() {
    return this.props.modals.selectFallback?.data?.selectedRow;
  }

  selectedRow() {
    const asset =
      this.getCurrentFallbackAsset() || this.getSavedFallbackAsset();
    return asset && [asset];
  }

  isSaveDisabled() {
    return !this.selectedRow();
  }

  saveAllowed(flags) {
    return !flags?.isReadonly && !this.isSaveDisabled();
  }

  clearAllowed() {
    return !!this.getSavedFallbackAsset();
  }

  footer(flags, session) {
    const { formatMessage } = this.props.intl;
    const footer = [];
    footer.push(
      <Button
        key="wizardCancel"
        label={formatMessage({ id: 'common.actions.cancel' })}
        variant="neutral"
        onClick={this.close.bind(this)}
      />
    );

    if (this.clearAllowed()) {
      footer.push(
        <Button
          key="wizardClearAll"
          className="wizard-clear-btn"
          label={formatMessage({ id: 'components.ecs.fallback.clearAll' })}
          variant="neutral"
          onClick={this.handleClearAll(session)}
        />
      );
    }

    footer.push(
      <Button
        className="wizard-next-btn"
        key="wizardFinish"
        label={formatMessage({ id: 'common.actions.save' })}
        variant="brand"
        disabled={!this.saveAllowed(flags)}
        onClick={this.handleFinish(session)}
      />
    );
    return footer;
  }

  render() {
    const assetType = this.assetType()?.uuid;
    const context = assetType ? `fallback_${assetType}` : undefined;
    const { formatMessage } = this.props.intl;
    const title = formatMessage({ id: 'components.ecs.fallback.wizardTitle' });

    return withSession(session => {
      return (
        <FlagContext.Consumer>
          {flags => (
            <Modal
              contentClassName="fallback"
              heading={title}
              onRequestClose={this.close.bind(this)}
              dismissOnClickOutside={false}
              directional={true}
              isOpen={this.isOpen()}
              size="medium"
              footer={this.footer(flags, session)}
            >
              <div className="fallback-asset-picker">
                <div className="wizard-instructions">
                  <FormattedMessage id="components.ecs.fallback.wizardInstructions" />
                </div>
                <AssetPicker
                  actions={this.props.actions}
                  assets={this.props.assets}
                  initialSelection={this.getSavedFallbackAsset()}
                  onRowSelect={selection => this.handleRowSelect(selection)}
                  context={context}
                  selectable
                  assetType={assetType}
                />
              </div>
            </Modal>
          )}
        </FlagContext.Consumer>
      );
    });
  }
}

SelectFallbackWizard.propTypes = {
  actions: PropTypes.object,
  assets: PropTypes.object,
  modals: PropTypes.object,
  fallback: PropTypes.object,
  location: PropTypes.object
};

export default injectIntl(SelectFallbackWizard);
